import { FC } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  SxProps,
  Card,
  CardContent
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";

import { lightTheme, useThemeMode } from "../../theme";
import { useTypedWord } from "./useTypedWord";

const moduleCardStyles: SxProps<typeof lightTheme> = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "box-shadow 0.3s ease-in-out",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.52)"
  }
};

export const Home: FC = () => {
  const navigate = useNavigate();
  const { toggleTechno } = useThemeMode();

  // Easter egg: typing "techno" on the home page toggles techno mode (components/technoMode).
  useTypedWord("techno", toggleTechno);

  return (
    <Box
      sx={{
        padding: 4,
        maxWidth: 1400,
        margin: "0 auto"
      }}
    >
      <Box sx={{ marginBottom: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          color="text.primary"
          sx={{
            fontWeight: 600,
            marginBottom: 1
          }}
        >
          Market Access and Counterparty Management System
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.6 }}
        >
          Centralized platform for managing trading counterparties, brokers, and
          trading country additions within MFT&apos;s trading operations.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card elevation={7} sx={moduleCardStyles}>
            <CardContent sx={{ padding: 3, flexGrow: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}
              >
                <HandshakeOutlinedIcon
                  sx={{
                    fontSize: 24,
                    color: "text.primary",
                    marginRight: 1.5
                  }}
                />
                <Typography
                  variant="h6"
                  component="span"
                  color="text.primary"
                  sx={{ fontWeight: 600 }}
                >
                  Trading Counterparties
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  marginBottom: 3,
                  lineHeight: 1.5
                }}
              >
                Manage and request new counterparty profiles and trading
                agreements.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate("/tradingcounterparties")}
                sx={{
                  marginTop: 2,
                  borderColor: "text.primary",
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "text.primary",
                    color: "white"
                  }
                }}
              >
                Go to module
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card elevation={7} sx={moduleCardStyles}>
            <CardContent sx={{ padding: 3, flexGrow: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}
              >
                <AccountBalanceOutlinedIcon
                  sx={{
                    fontSize: 24,
                    color: "text.primary",
                    marginRight: 1.5
                  }}
                />
                <Typography
                  variant="h6"
                  component="span"
                  color="text.primary"
                  sx={{ fontWeight: 600 }}
                >
                  Broker Network
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  marginBottom: 3,
                  lineHeight: 1.5
                }}
              >
                Manage and request new trading brokers and agreements.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate("/brokers")}
                sx={{
                  marginTop: 2,
                  borderColor: "text.primary",
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "text.primary",
                    color: "white"
                  }
                }}
              >
                Go to module
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card elevation={7} sx={moduleCardStyles}>
            <CardContent sx={{ padding: 3, flexGrow: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}
              >
                <PublicOutlinedIcon
                  sx={{
                    fontSize: 24,
                    color: "text.primary",
                    marginRight: 1.5
                  }}
                />
                <Typography
                  variant="h6"
                  component="span"
                  color="text.primary"
                  sx={{ fontWeight: 600 }}
                >
                  Country Addition
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  marginBottom: 3,
                  lineHeight: 1.5
                }}
              >
                Request and track everything a country needs before MFT can
                trade there.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate("/countries")}
                sx={{
                  marginTop: 2,
                  borderColor: "text.primary",
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "text.primary",
                    color: "white"
                  }
                }}
              >
                Go to module
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
